/**
 * /control/archivo-concentracion · Fase 18.3
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { FolderClosed, Plus, RefreshCw, AlertTriangle, Archive } from "lucide-react";
import { AC_ESTADOS, fmtFecha } from "./_common";

export default function ArchivoConcentracion() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [fids, setFids] = useState([]);
    const [form, setForm] = useState({
        fideicomiso_id: "",
        custodio_responsable: "",
        ubicacion_fisica: "Bóveda central CDMX Piso 8",
        años_retencion_obligatoria: 10,
        notas: "",
    });

    const load = async () => {
        const r = await api.get("/control/archivo-concentracion");
        setItems(r.data || []);
    };
    const loadFids = async () => {
        try {
            const r = await api.get("/fideicomisos");
            const arr = Array.isArray(r.data) ? r.data : r.data.items || [];
            // Prioritizar Extinción
            setFids(arr.map(f => ({
                id: f.id,
                label: f.numero_fideicomiso || f.numero_interno || f.id,
                estado_ciclo: f.estado_ciclo,
            })));
        } catch { setFids([]); }
    };
    useEffect(() => { load(); loadFids(); }, []);

    const transferir = async () => {
        try {
            await api.post("/control/archivo-concentracion/transferir", form);
            toast.success("Expediente transferido al archivo de concentración");
            setOpen(false); load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        }
    };

    return (
        <div className="space-y-6" data-testid="ac-list">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                        Archivo de Concentración · M-16
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Retención documental de expedientes concluidos · Expurgo controlado tras {" "}
                        <b>N años</b> de retención obligatoria (default 10).
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={load} data-testid="ac-refresh">
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
                                data-testid="ac-nuevo">
                                <Plus className="h-4 w-4 mr-1" /> Transferir
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Transferir expediente al archivo</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3">
                                <div>
                                    <label className="text-xs font-semibold">
                                        Fideicomiso (debe estar en Extinción)
                                    </label>
                                    <Select value={form.fideicomiso_id}
                                        onValueChange={v => setForm({ ...form, fideicomiso_id: v })}>
                                        <SelectTrigger data-testid="ac-sel-fid"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {fids.map(f => (
                                                <SelectItem key={f.id} value={f.id}>
                                                    {f.label} <span className="text-xs text-muted-foreground">· {f.estado_ciclo}</span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Custodio responsable</label>
                                    <Input value={form.custodio_responsable}
                                        onChange={e => setForm({ ...form, custodio_responsable: e.target.value })}
                                        placeholder="Nombre + puesto" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-semibold">Ubicación física</label>
                                        <Input value={form.ubicacion_fisica}
                                            onChange={e => setForm({ ...form, ubicacion_fisica: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold">Años retención</label>
                                        <Input type="number" min="5" max="30" value={form.años_retencion_obligatoria}
                                            onChange={e => setForm({ ...form, años_retencion_obligatoria: Number(e.target.value) })} />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Notas</label>
                                    <Textarea value={form.notas} rows={2}
                                        onChange={e => setForm({ ...form, notas: e.target.value })} />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                                <Button onClick={transferir} data-testid="ac-transferir-btn">Transferir</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <Card>
                <CardContent className="p-0">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">Folio expediente</th>
                                <th className="px-3 py-2 text-left">Transferido</th>
                                <th className="px-3 py-2 text-left">Expurgo prog.</th>
                                <th className="px-3 py-2 text-left">Custodio</th>
                                <th className="px-3 py-2 text-left">Ubicación</th>
                                <th className="px-3 py-2 text-left">Estado</th>
                                <th className="px-3 py-2 text-right">Accesos</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">
                                    Sin expedientes archivados. Transfiere al concluir un fideicomiso.
                                </td></tr>
                            )}
                            {items.map(ac => {
                                const cfg = AC_ESTADOS[ac.estado] || { label: ac.estado, cls: "" };
                                return (
                                    <tr key={ac.id} className="border-t">
                                        <td className="px-3 py-2 font-mono text-xs">{ac.folio_expediente_concentrado}</td>
                                        <td className="px-3 py-2 text-xs">{fmtFecha(ac.fecha_transferencia)}</td>
                                        <td className="px-3 py-2 text-xs">{fmtFecha(ac.fecha_expurgo_programado)}</td>
                                        <td className="px-3 py-2 text-xs">{ac.custodio_responsable}</td>
                                        <td className="px-3 py-2 text-xs">{ac.ubicacion_fisica}</td>
                                        <td className="px-3 py-2"><Badge className={cfg.cls}>{cfg.label}</Badge></td>
                                        <td className="px-3 py-2 text-right text-xs">
                                            {(ac.accesos_registrados || []).length}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </CardContent>
            </Card>

            <p className="text-xs text-muted-foreground flex items-start gap-1">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                Expurgo bloqueado antes de la fecha programada · Requiere doble firma (DG + Jurídico Senior) + acta de destrucción.
            </p>
        </div>
    );
}
